/**
 * Settings → MOU → Document templates.
 *
 * A template is the legal wording every MOU prepared from it will carry, so
 * editing is for MOU managers and super admins only (the server enforces it;
 * `can_manage` just decides whether to show the buttons).
 *
 * Changing a template never touches an MOU that already exists — each MOU
 * keeps the text it was rendered with, which is the point of the snapshot.
 */

import { useCallback, useEffect, useState } from "react";
import { Copy, Eye, FileText, Loader2, Pencil, Plus, Star, Trash2, X } from "lucide-react";

import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { MOU_TEMPLATES_URL, MOU_TEMPLATE_PREVIEW_URL, MOU_TEMPLATE_URL } from "../../constants/api";
import HeaderFooterEditor, { isBlankHtml } from "./HeaderFooterEditor";
import MergeFieldEditor from "./MergeFieldEditor";
import EditorWorkspace from "./EditorWorkspace";
import { PdfPreviewModal, downloadBlob } from "./pdfPreview";

const BLANK = { id: null, name: "", description: "", title: "", body_html: "",
                header_html: "", footer_html: "", is_default: false, scope: "organisation" };

export default function MOUTemplateManager() {
  const { fetch, create, update, remove } = useApiMethods();

  const [data, setData]       = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving]   = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  const load = useCallback(() => {
    fetch(MOU_TEMPLATES_URL, { full: 1 }).then((res) => {
      if (res?.status === "success") setData(res.data);
      else ToastMsg("error", res?.message || "Could not load templates");
    });
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  // The template as the real PDF, filled with sample values — the only exact
  // answer to "which page is this on?".
  const preview = async () => {
    setPreviewing(true);
    try {
      setPreviewUrl(await downloadBlob(MOU_TEMPLATE_PREVIEW_URL, null, {
        method: "post",
        body: { body_html: editing.body_html, header_html: editing.header_html,
                footer_html: editing.footer_html, title: editing.title },
      }));
    } catch (e) {
      ToastMsg("error", e.message || "Could not build the preview");
    } finally {
      setPreviewing(false);
    }
  };

  const save = async () => {
    if (!editing.name.trim()) { ToastMsg("error", "Give the template a name"); return; }
    if (!editing.body_html.replace(/<[^>]+>/g, "").trim()) { ToastMsg("error", "The template has no text"); return; }
    setSaving(true);
    const res = editing.id
      ? await update(MOU_TEMPLATE_URL(editing.id), editing)
      : await create(MOU_TEMPLATES_URL, editing);
    setSaving(false);
    if (res?.status === "success") { ToastMsg("success", "Template saved"); setEditing(null); load(); }
    else ToastMsg("error", res?.message || "Failed to save");
  };

  const del = async (t) => {
    if (!window.confirm(`Delete the template "${t.name}"? MOUs already prepared from it keep their text.`)) return;
    const res = await remove(MOU_TEMPLATE_URL(t.id));
    if (res?.status === "success") { ToastMsg("success", "Template deleted"); load(); }
    else ToastMsg("error", res?.message || "Failed to delete");
  };

  const makeDefault = async (t) => {
    const res = await update(MOU_TEMPLATE_URL(t.id), { is_default: true });
    if (res?.status === "success") load();
    else ToastMsg("error", res?.message || "Failed to update");
  };

  if (!data) {
    return (
      <div className="flex items-center gap-2 p-4 text-xs text-gray-400">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading templates…
      </div>
    );
  }

  const canManage = data.can_manage;

  return (
    <section className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 text-sm font-medium text-gray-800 dark:text-gray-100">
            <FileText className="h-3.5 w-3.5 text-gray-400" /> Document templates
          </h3>
          <p className="mt-1 max-w-2xl text-xs leading-relaxed text-gray-500 dark:text-gray-400">
            The wording an MOU document starts from. Fields like{" "}
            <code className="rounded bg-gray-100 px-1 dark:bg-gray-700">{"{{client_name}}"}</code> and{" "}
            <code className="rounded bg-gray-100 px-1 dark:bg-gray-700">{"{{contract_value}}"}</code> are
            filled from the MOU when it is prepared. The default is what a new MOU opens with.
          </p>
        </div>
        {canManage && !editing && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm"
                    onClick={() => setEditing({ ...BLANK, name: data.builtin.name, body_html: data.builtin.body_html })}>
              <Copy className="mr-1 h-3.5 w-3.5" /> Start from standard
            </Button>
            <Button size="sm" onClick={() => setEditing({ ...BLANK })}>
              <Plus className="mr-1 h-3.5 w-3.5" /> New template
            </Button>
          </div>
        )}
      </div>

      {!canManage && (
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
          Only an MOU manager or a super admin can change templates.
        </p>
      )}

      {editing ? (
        <div className="mt-4 space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <label className="text-xs font-medium">Name <span className="text-red-500">*</span></label>
              <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                     placeholder="e.g. Hospital HIS — 3 year" />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium">Description</label>
              <Input value={editing.description}
                     onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-4 text-xs">
            <label className="flex cursor-pointer items-center gap-1.5">
              <input type="checkbox" className="h-4 w-4 accent-primary" checked={!!editing.is_default}
                     onChange={(e) => setEditing({ ...editing, is_default: e.target.checked })} />
              Use as the default for new MOUs
            </label>
            {!editing.id && (
              <label className="flex cursor-pointer items-center gap-1.5">
                <input type="checkbox" className="h-4 w-4 accent-primary"
                       checked={editing.scope === "project"}
                       onChange={(e) => setEditing({ ...editing, scope: e.target.checked ? "project" : "organisation" })} />
                Only for the current project
              </label>
            )}
          </div>
          <HeaderFooterEditor header={editing.header_html} footer={editing.footer_html}
                              fields={data.merge_fields} bandFields={data.band_fields || []}
                              onChange={(c) => setEditing((p) => ({
                                ...p,
                                ...("header" in c ? { header_html: c.header } : {}),
                                ...("footer" in c ? { footer_html: c.footer } : {}),
                              }))} />
          <p className="text-[11px] text-gray-500 dark:text-gray-400">
            The editor shows the page width of the PDF and marks where each page should end.
            Use <b>Signature block</b> at the end of each annexure so both parties sign it;
            <b> Preview PDF</b> shows the exact pages with sample values.
          </p>
          <EditorWorkspace storageKey="template"
                           contentKey={`${editing.title || ""}|${editing.header_html || ""}|${editing.footer_html || ""}|${editing.body_html || ""}`}
                           buildPreview={() => downloadBlob(MOU_TEMPLATE_PREVIEW_URL, null, {
                             method: "post",
                             body: { body_html: editing.body_html, header_html: editing.header_html,
                                     footer_html: editing.footer_html, title: editing.title },
                           })}>
            {({ height, onCursorPage }) => (
              <MergeFieldEditor value={editing.body_html} fields={data.merge_fields}
                                layoutFields={data.layout_fields || []} tables onCursorPage={onCursorPage}
                                pageGuides={{ customHeader: !isBlankHtml(editing.header_html),
                                              customFooter: !isBlankHtml(editing.footer_html),
                                              header: isBlankHtml(editing.header_html) ? "" : editing.header_html,
                                              footer: isBlankHtml(editing.footer_html) ? "" : editing.footer_html }}
                                onChange={(v) => setEditing((p) => ({ ...p, body_html: v }))} height={height} />
            )}
          </EditorWorkspace>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" disabled={previewing} onClick={preview}>
              {previewing ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                          : <Eye className="mr-1 h-3.5 w-3.5" />}
              Preview PDF
            </Button>
            <Button variant="outline" size="sm" onClick={() => setEditing(null)}>
              <X className="mr-1 h-3.5 w-3.5" /> Cancel
            </Button>
            <Button size="sm" disabled={saving} onClick={save}>
              {saving && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
              Save template
            </Button>
          </div>
        </div>
      ) : (
        <ul className="mt-3 divide-y divide-gray-100 rounded-md border border-gray-100 dark:divide-gray-700 dark:border-gray-700">
          {data.templates.length === 0 && (
            <li className="px-3 py-4 text-center text-xs text-gray-500 dark:text-gray-400">
              No templates yet — new MOUs use the built-in standard wording until you add one.
            </li>
          )}
          {data.templates.map((t) => (
            <li key={t.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5">
              <div className="min-w-0">
                <p className="flex items-center gap-1.5 text-sm font-medium text-gray-900 dark:text-white">
                  {t.name}
                  {t.is_default && (
                    <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                      <Star className="h-2.5 w-2.5" /> Default
                    </span>
                  )}
                  <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                    {t.scope === "project" ? "This project" : "All projects"}
                  </span>
                </p>
                {t.description && <p className="text-[11px] text-gray-500 dark:text-gray-400">{t.description}</p>}
              </div>
              {canManage && (
                <div className="flex items-center gap-1">
                  {!t.is_default && (
                    <Button variant="ghost" size="sm" onClick={() => makeDefault(t)} title="Make default">
                      <Star className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" title="Edit"
                          onClick={() => setEditing({ ...BLANK, ...t })}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="sm" title="Duplicate"
                          onClick={() => setEditing({ ...BLANK, ...t, id: null, name: `${t.name} (copy)`, is_default: false })}>
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="sm" title="Delete" onClick={() => del(t)}>
                    <Trash2 className="h-3.5 w-3.5 text-red-500" />
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      <PdfPreviewModal url={previewUrl} title={`Preview — ${editing?.name || "template"} (sample values)`}
                       onClose={() => setPreviewUrl(null)} />
    </section>
  );
}
